import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Whether the current slide change was initiated by user interaction.
   * @type {boolean}
   */
  #userInitiated = false;

  /**
   * Visually-hidden live region for screen reader announcements.
   * Toggling aria-hidden on existing slides doesn't reliably trigger
   * NVDA announcements, so we use a dedicated live region and push
   * the slide text into it on user-initiated navigation.
   * @type {HTMLElement|null}
   */
  #liveRegion = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#createLiveRegion();
    this.play();
  }

  /**
   * Creates a visually-hidden aria-live region for announcing slide
   * changes to screen readers on user-initiated navigation only.
   */
  #createLiveRegion() {
    this.#liveRegion = document.createElement('div');
    this.#liveRegion.setAttribute('aria-live', 'polite');
    this.#liveRegion.setAttribute('aria-atomic', 'true');
    this.#liveRegion.setAttribute('role', 'status');
    this.#liveRegion.className = 'visually-hidden';
    this.appendChild(this.#liveRegion);
  }

  next() {
    this.#userInitiated = true;
    this.current += 1;
    this.#userInitiated = false;
  }

  previous() {
    this.#userInitiated = true;
    this.current -= 1;
    this.#userInitiated = false;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.current += 1;
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const isHidden = index !== relativeIndex;
      slide.setAttribute('aria-hidden', `${isHidden}`);
      slide.inert = isHidden;
    });

    // For user-initiated changes (arrow clicks), push the new slide's
    // text into the dedicated live region so NVDA announces it.
    // Auto-rotation skips this to avoid interrupting users.
    if (this.#userInitiated && this.#liveRegion) {
      const activeSlide = this.refs.slides?.[relativeIndex];
      const text = activeSlide?.textContent?.trim() ?? '';
      const total = (this.refs.slides ?? []).length;
      this.#liveRegion.textContent = `${relativeIndex + 1} of ${total}: ${text}`;
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
